import {
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Checkbox,
  Group,
  Modal,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconCheck,
  IconCircleCheck,
  IconCircleMinus,
  IconCirclePlus,
  IconCurrencyDollar,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LogoImg } from "../../components/LogoImg";
import { MOCK_BRANDS } from "../../mock/brands";
import { MOCK_CATALOG_REFRESH_DIFFS } from "../../mock/catalog-refresh";
import type {
  CatalogReviewAction,
  MsrpChange,
  NewProduct,
  RemovedProduct,
  UnchangedProduct,
} from "../../types/catalog-refresh";
import { formatPercentChange, formatPriceChange, formatPriceCompact, formatPriceRange, getDeltaColor } from "../../utils/price";

// --- Constants ---

type TabValue = "new" | "removed" | "unchanged" | "msrp_changed";

const VALID_TABS = new Set<TabValue>(["new", "removed", "unchanged", "msrp_changed"]);

function parseTab(raw: string | null): TabValue {
  return (VALID_TABS as Set<string>).has(raw ?? "") ? (raw as TabValue) : "new";
}

const PAGE_SIZE = 50;
const CRITICAL_MISS_THRESHOLD = 3;
const TERMINAL_ACTIONS = new Set<CatalogReviewAction>(["track", "ignore"]);

const NEW_PRODUCT_OPTIONS = [
  { label: "Track", value: "track" },
  { label: "Ignore", value: "ignore" },
  { label: "Later", value: "decide_later" },
];

const REMOVED_PRODUCT_OPTIONS = [
  { label: "Discontinue", value: "ignore" },
  { label: "Keep", value: "track" },
  { label: "Later", value: "decide_later" },
];

const MSRP_CHANGE_OPTIONS = [
  { label: "Accept", value: "track" },
  { label: "Ignore", value: "ignore" },
  { label: "Later", value: "decide_later" },
];

// --- Page ---

export function RefreshReviewPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = parseTab(searchParams.get("tab"));

  const brand = MOCK_BRANDS.find((b) => b.id === brandId);
  const diff = brandId ? MOCK_CATALOG_REFRESH_DIFFS[brandId] : undefined;

  // Shared search/filter state
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // Local state for review actions
  const [newActions, setNewActions] = useState<Record<string, CatalogReviewAction>>({});
  const [removedActions, setRemovedActions] = useState<Record<string, CatalogReviewAction>>({});
  const [msrpActions, setMsrpActions] = useState<Record<string, CatalogReviewAction>>({});

  // Track confirmation modal
  const [confirmModalOpened, { open: openConfirmModal, close: closeConfirmModal }] = useDisclosure(false);
  const [pendingTrackIds, setPendingTrackIds] = useState<string[]>([]);

  // Will be derived from API response when wired
  const crawlFailed = false;

  const newProducts = useMemo(
    () => (diff?.newProducts ?? []).map((p) => ({ ...p, reviewStatus: newActions[p.id] ?? p.reviewStatus })),
    [diff, newActions],
  );

  const removedProducts = useMemo(
    () =>
      (diff?.removedProducts ?? []).map((p) => ({ ...p, reviewStatus: removedActions[p.id] ?? p.reviewStatus })),
    [diff, removedActions],
  );

  const msrpChanges = useMemo(
    () => (diff?.msrpChanges ?? []).map((p) => ({ ...p, reviewStatus: msrpActions[p.id] ?? p.reviewStatus })),
    [diff, msrpActions],
  );

  // Category options from new products for filtering
  const categoryOptions = useMemo(() => {
    const categories = new Set<string>();
    for (const p of diff?.newProducts ?? []) categories.add(p.oemCategory);
    return [...categories].sort().map((c) => ({ value: c, label: c }));
  }, [diff]);

  const totalItems =
    (diff?.newProducts.length ?? 0) + (diff?.removedProducts.length ?? 0) + (diff?.msrpChanges.length ?? 0);

  const decidedItems = useMemo(
    () =>
      Object.values(newActions).filter((v) => TERMINAL_ACTIONS.has(v)).length +
      Object.values(removedActions).filter((v) => TERMINAL_ACTIONS.has(v)).length +
      Object.values(msrpActions).filter((v) => TERMINAL_ACTIONS.has(v)).length,
    [newActions, removedActions, msrpActions],
  );

  function setTab(tab: string | null) {
    if (tab) setSearchParams({ tab });
  }

  function handleBulkTrack(ids: string[]) {
    setPendingTrackIds(ids);
    openConfirmModal();
  }

  function confirmBulkTrack() {
    setNewActions((prev) => {
      const next = { ...prev };
      for (const id of pendingTrackIds) next[id] = "track";
      return next;
    });
    closeConfirmModal();
    notifications.show({
      title: "Products tracked",
      message: `${pendingTrackIds.length} product(s) marked for tracking.`,
      color: "green",
    });
    setPendingTrackIds([]);
  }

  function handleBulkAction(
    ids: string[],
    action: CatalogReviewAction,
  ) {
    if (action === "track") {
      handleBulkTrack(ids);
      return;
    }
    setNewActions((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = action;
      return next;
    });
  }

  // --- Not found ---
  if (!brand || !diff) {
    return (
      <>
        <Title order={2} mb="md">
          Catalog Review
        </Title>
        <Text c="dimmed">
          {!brand ? "Brand not found." : "No pending catalog refresh for this brand."}
        </Text>
      </>
    );
  }

  // --- Error state: crawl failed (before empty-state — a failed crawl also has totalItems === 0) ---
  if (crawlFailed) {
    return (
      <>
        <ReviewBreadcrumbs brandName={brand.name} />
        <Stack align="center" gap="md" mt="xl">
          <ThemeIcon size={64} radius="xl" color="red" variant="light">
            <IconAlertTriangle size={36} />
          </ThemeIcon>
          <Title order={3}>Crawl Failed</Title>
          <Text c="dimmed" ta="center" maw={400}>
            The OEM catalog refresh for {brand.name} encountered an error. Check the adapter health on the
            Brands page or retry the crawl.
          </Text>
          <Group>
            <Button variant="light" onClick={() => navigate("/admin/brands")}>
              Back to Brands
            </Button>
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={() => notifications.show({ title: "Retry queued", message: "Crawl retry is not yet implemented.", color: "yellow" })}
            >
              Retry Crawl
            </Button>
          </Group>
        </Stack>
      </>
    );
  }

  // --- Empty state: catalog unchanged ---
  if (totalItems === 0) {
    return (
      <>
        <ReviewBreadcrumbs brandName={brand.name} />
        <Stack align="center" gap="md" mt="xl">
          <ThemeIcon size={64} radius="xl" color="green" variant="light">
            <IconCheck size={36} />
          </ThemeIcon>
          <Title order={3}>Catalog Unchanged</Title>
          <Text c="dimmed" ta="center" maw={400}>
            The latest OEM catalog refresh found no new products, removals, or MSRP changes for{" "}
            {brand.name}.
          </Text>
          <Button variant="light" onClick={() => navigate("/admin/brands")}>
            Back to Brands
          </Button>
        </Stack>
      </>
    );
  }

  return (
    <>
      <ReviewBreadcrumbs brandName={brand.name} />

      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <LogoImg domain={brand.domain} alt={brand.name} size={28} />
          <Title order={2}>{brand.name} Catalog Review</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Refreshed {new Date(diff.refreshedAt).toLocaleDateString("en-US")} in {diff.durationSeconds}s
        </Text>
      </Group>

      {/* Search + Category filter */}
      <Group gap="sm" mb="md">
        <TextInput
          placeholder="Search products..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1 }}
          size="sm"
        />
        {activeTab === "new" && categoryOptions.length > 1 && (
          <Select
            placeholder="Category"
            data={categoryOptions}
            value={categoryFilter}
            onChange={setCategoryFilter}
            clearable
            size="sm"
            w={200}
          />
        )}
      </Group>

      <Tabs value={activeTab} onChange={setTab}>
        <Tabs.List mb="md">
          <Tabs.Tab
            value="new"
            leftSection={<IconCirclePlus size={16} />}
            rightSection={
              <Badge size="xs" variant="filled" color="blue">
                {diff.summary.newCount}
              </Badge>
            }
          >
            New
          </Tabs.Tab>
          <Tabs.Tab
            value="removed"
            leftSection={<IconCircleMinus size={16} />}
            rightSection={
              <Badge size="xs" variant="filled" color="orange">
                {diff.summary.removedCount}
              </Badge>
            }
          >
            Removed
          </Tabs.Tab>
          <Tabs.Tab
            value="msrp_changed"
            leftSection={<IconCurrencyDollar size={16} />}
            rightSection={
              <Badge size="xs" variant="filled" color="orange">
                {diff.summary.msrpChangedCount}
              </Badge>
            }
          >
            MSRP Changed
          </Tabs.Tab>
          <Tabs.Tab
            value="unchanged"
            leftSection={<IconCircleCheck size={16} />}
            rightSection={
              <Badge size="xs" variant="outline" color="gray">
                {diff.summary.unchangedCount}
              </Badge>
            }
          >
            Unchanged
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="new">
          {activeTab === "new" && (
            <NewProductsPanel
              products={newProducts}
              search={debouncedSearch}
              categoryFilter={categoryFilter}
              onAction={(id, action) => setNewActions((prev) => ({ ...prev, [id]: action }))}
              onBulkAction={handleBulkAction}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="removed">
          {activeTab === "removed" && (
            <RemovedProductsPanel
              products={removedProducts}
              search={debouncedSearch}
              onAction={(id, action) => setRemovedActions((prev) => ({ ...prev, [id]: action }))}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="msrp_changed">
          {activeTab === "msrp_changed" && (
            <MsrpChangesPanel
              changes={msrpChanges}
              search={debouncedSearch}
              onAction={(id, action) => setMsrpActions((prev) => ({ ...prev, [id]: action }))}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="unchanged">
          {activeTab === "unchanged" && (
            <UnchangedProductsPanel products={diff.unchangedProducts} search={debouncedSearch} />
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Summary footer */}
      <Group
        justify="space-between"
        mt="xl"
        pt="md"
        style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
      >
        <Text size="sm" c="dimmed">
          {totalItems - decidedItems} of {totalItems} items still need a decision
        </Text>
        <Group gap="xs">
          <Button variant="default" size="sm" onClick={() => navigate("/admin/brands")}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={decidedItems === 0}
            onClick={() => notifications.show({ title: "Review submitted", message: `${decidedItems} decision(s) recorded. Submit is not yet wired to the API.`, color: "blue" })}
          >
            Submit Review
          </Button>
        </Group>
      </Group>

      {/* Track confirmation modal */}
      <Modal opened={confirmModalOpened} onClose={closeConfirmModal} title="Confirm Tracking" size="sm">
        <Text size="sm" mb="md">
          Are you sure you want to track {pendingTrackIds.length} product(s)? This will create new product
          lines and begin price monitoring.
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button variant="default" size="sm" onClick={closeConfirmModal}>
            Cancel
          </Button>
          <Button size="sm" onClick={confirmBulkTrack}>
            Confirm Track
          </Button>
        </Group>
      </Modal>
    </>
  );
}

// --- Shared Components ---

function ReviewBreadcrumbs({ brandName }: { brandName: string }) {
  const navigate = useNavigate();
  return (
    <Breadcrumbs mb="md">
      <Anchor size="sm" onClick={() => navigate("/admin/brands")}>
        Brands
      </Anchor>
      <Text size="sm">{brandName}</Text>
      <Text size="sm">Catalog Review</Text>
    </Breadcrumbs>
  );
}

interface ColumnMeta {
  align?: "left" | "center" | "right";
  width?: number;
}

function useSearchFilter<T>(items: T[], search: string, getText: (item: T) => string): T[] {
  return useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((item) => getText(item).toLowerCase().includes(q));
  }, [items, search, getText]);
}

function useReviewTable<T extends { id: string }>(
  data: T[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any[],
) {
  // eslint-disable-next-line react-hooks/incompatible-library
  return useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });
}

function ReviewTable<T>({
  table,
  highlightPending = false,
  highlightOnHover = true,
  minWidth = 800,
  emptyMessage = "No items match your search.",
}: {
  table: ReturnType<typeof useReactTable<T>>;
  highlightPending?: boolean;
  highlightOnHover?: boolean;
  minWidth?: number;
  emptyMessage?: string;
}) {
  const columnCount = table.getAllColumns().length;
  return (
    <>
      <Table.ScrollContainer minWidth={minWidth}>
        <Table highlightOnHover={highlightOnHover} verticalSpacing="sm" fz="sm">
          <Table.Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as ColumnMeta | undefined;
                  return (
                    <Table.Th key={header.id} ta={meta?.align} w={meta?.width}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {table.getRowModel().rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={columnCount} ta="center" py="xl">
                  <Text c="dimmed">{emptyMessage}</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Table.Tr
                  key={row.id}
                  style={
                    highlightPending && (row.original as Record<string, unknown>).reviewStatus == null
                      ? { background: "var(--mantine-color-yellow-0)" }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as ColumnMeta | undefined;
                    return (
                      <Table.Td key={cell.id} ta={meta?.align}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <TablePagination table={table} />
    </>
  );
}

function TablePagination<T>({ table }: { table: ReturnType<typeof useReactTable<T>> }) {
  const pageCount = table.getPageCount();
  if (pageCount <= 1) return null;

  return (
    <Group justify="space-between" mt="md">
      <Text size="xs" c="dimmed">
        {table.getFilteredRowModel().rows.length} total
      </Text>
      <Pagination
        total={pageCount}
        value={table.getState().pagination.pageIndex + 1}
        onChange={(page) => table.setPageIndex(page - 1)}
        size="sm"
      />
    </Group>
  );
}

// --- New Products Panel ---

const newColumnHelper = createColumnHelper<NewProduct>();

function NewProductsPanel({
  products,
  search,
  categoryFilter,
  onAction,
  onBulkAction,
}: {
  products: NewProduct[];
  search: string;
  categoryFilter: string | null;
  onAction: (id: string, action: CatalogReviewAction) => void;
  onBulkAction: (ids: string[], action: CatalogReviewAction) => void;
}) {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // Clear selection when filters change so users don't bulk-act on hidden rows
  useEffect(() => {
    setRowSelection({});
  }, [search, categoryFilter]);

  const getNewProductText = useCallback((p: NewProduct) => `${p.name} ${p.oemCategory}`, []);
  const searchFiltered = useSearchFilter(products, search, getNewProductText);
  const filtered = useMemo(
    () => (categoryFilter ? searchFiltered.filter((p) => p.oemCategory === categoryFilter) : searchFiltered),
    [searchFiltered, categoryFilter],
  );

  const columns = useMemo(
    () => [
      newColumnHelper.display({
        id: "select",
        meta: { width: 40 } as ColumnMeta,
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
            aria-label="Select all"
            size="xs"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            aria-label={`Select ${row.original.name}`}
            size="xs"
          />
        ),
      }),
      newColumnHelper.accessor("name", {
        header: "Product",
        cell: (info) => (
          <Text size="sm" fw={500}>
            {info.getValue()}
          </Text>
        ),
      }),
      newColumnHelper.accessor("oemCategory", {
        header: "Category",
        cell: (info) => (
          <Text size="xs" c="dimmed">
            {info.getValue()}
          </Text>
        ),
      }),
      newColumnHelper.accessor("variants", {
        header: "Variants",
        meta: { align: "center" } as ColumnMeta,
        cell: (info) => (
          <Group gap={4} justify="center">
            {info.getValue().map((v) => (
              <Badge key={v.label} variant="light" color="gray" size="xs">
                {v.label}
              </Badge>
            ))}
          </Group>
        ),
      }),
      newColumnHelper.accessor("oemPriceRange", {
        header: "OEM Price Range",
        meta: { align: "right" } as ColumnMeta,
        cell: (info) => {
          const range = info.getValue();
          return (
            <Text size="sm">
              {range ? formatPriceRange(range.min, range.max) : "—"}
            </Text>
          );
        },
      }),
      newColumnHelper.accessor("matchStatus", {
        header: "Match",
        cell: (info) => {
          const row = info.row.original;
          return row.matchStatus === "possible_duplicate" ? (
            <Tooltip label={`Possible match: ${row.matchedProductLineName}`}>
              <Badge variant="light" color="yellow" size="xs">
                Possible duplicate
              </Badge>
            </Tooltip>
          ) : (
            <Badge variant="light" color="green" size="xs">
              New
            </Badge>
          );
        },
      }),
      newColumnHelper.accessor("reviewStatus", {
        header: "Action",
        meta: { align: "center" } as ColumnMeta,
        cell: (info) => {
          const row = info.row.original;
          return (
            <SegmentedControl
              size="xs"
              data={NEW_PRODUCT_OPTIONS}
              value={row.reviewStatus == null ? "decide_later" : row.reviewStatus}
              onChange={(val) => onAction(row.id, val as CatalogReviewAction)}
              aria-label={`Action for ${row.name}`}
            />
          );
        },
      }),
    ],
    [onAction],
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: filtered,
    columns,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);

  if (products.length === 0) {
    return (
      <Text c="dimmed" mt="md">
        No new products found in this refresh.
      </Text>
    );
  }

  return (
    <>
      {selectedIds.length > 0 && (
        <Group gap="xs" mb="sm">
          <Text size="xs" c="dimmed">
            {selectedIds.length} selected:
          </Text>
          <Button size="xs" variant="light" color="green" onClick={() => onBulkAction(selectedIds, "track")}>
            Track All
          </Button>
          <Button size="xs" variant="light" color="gray" onClick={() => onBulkAction(selectedIds, "ignore")}>
            Ignore All
          </Button>
          <Button
            size="xs"
            variant="light"
            color="gray"
            onClick={() => onBulkAction(selectedIds, "decide_later")}
          >
            Decide Later
          </Button>
        </Group>
      )}
      <ReviewTable table={table} highlightPending emptyMessage="No products match your search." />
    </>
  );
}

// --- Removed Products Panel ---

const removedColumnHelper = createColumnHelper<RemovedProduct>();

const getRemovedText = (p: RemovedProduct) => p.name;

function RemovedProductsPanel({
  products,
  search,
  onAction,
}: {
  products: RemovedProduct[];
  search: string;
  onAction: (id: string, action: CatalogReviewAction) => void;
}) {
  const filtered = useSearchFilter(products, search, getRemovedText);

  const columns = useMemo(
    () => [
      removedColumnHelper.accessor("name", {
        header: "Product",
        cell: (info) => (
          <Text size="sm" fw={500}>
            {info.getValue()}
          </Text>
        ),
      }),
      removedColumnHelper.accessor("lastSeenAt", {
        header: "Last Seen",
        cell: (info) => (
          <Text size="xs" c="dimmed">
            {new Date(info.getValue()).toLocaleDateString("en-US")}
          </Text>
        ),
      }),
      removedColumnHelper.accessor("consecutiveMisses", {
        header: () => (
          <Tooltip label="Number of consecutive OEM catalog refreshes where this product was not found">
            <Text
              size="xs"
              fw={600}
              style={{ cursor: "help", borderBottom: "1px dotted var(--mantine-color-gray-5)" }}
            >
              Consecutive Misses
            </Text>
          </Tooltip>
        ),
        meta: { align: "center" } as ColumnMeta,
        cell: (info) => (
          <Badge variant="light" color={info.getValue() >= CRITICAL_MISS_THRESHOLD ? "red" : "yellow"} size="sm">
            {info.getValue()}
          </Badge>
        ),
      }),
      removedColumnHelper.accessor("stillAtRetailers", {
        header: "Retailer Presence",
        cell: (info) => (
          <Group gap={6}>
            {info.getValue().map((r) => (
              <Tooltip key={r.retailerId} label={`${r.retailerName}: ${r.hasListing ? "Listed" : "Not found"}`}>
                <LogoImg domain={r.domain} alt={r.retailerName} size={16} style={{ opacity: r.hasListing ? 1 : 0.2 }} />
              </Tooltip>
            ))}
          </Group>
        ),
      }),
      removedColumnHelper.accessor("reviewStatus", {
        header: "Action",
        meta: { align: "center" } as ColumnMeta,
        cell: (info) => {
          const row = info.row.original;
          return (
            <SegmentedControl
              size="xs"
              data={REMOVED_PRODUCT_OPTIONS}
              value={row.reviewStatus == null ? "decide_later" : row.reviewStatus}
              onChange={(val) => onAction(row.id, val as CatalogReviewAction)}
              aria-label={`Action for ${row.name}`}
            />
          );
        },
      }),
    ],
    [onAction],
  );

  const table = useReviewTable(filtered, columns);

  if (products.length === 0) {
    return (
      <Text c="dimmed" mt="md">
        No products removed in this refresh.
      </Text>
    );
  }

  return <ReviewTable table={table} highlightPending emptyMessage="No products match your search." />;
}

// --- Unchanged Products Panel ---

const unchangedColumnHelper = createColumnHelper<UnchangedProduct>();

const getUnchangedText = (p: UnchangedProduct) => p.name;

function UnchangedProductsPanel({
  products,
  search,
}: {
  products: UnchangedProduct[];
  search: string;
}) {
  const filtered = useSearchFilter(products, search, getUnchangedText);

  const columns = useMemo(
    () => [
      unchangedColumnHelper.accessor("name", {
        header: "Product",
        cell: (info) => <Text size="sm">{info.getValue()}</Text>,
      }),
      unchangedColumnHelper.accessor("variantCount", {
        header: "Variants",
        meta: { align: "center" } as ColumnMeta,
        cell: (info) => (
          <Text size="sm" c="dimmed">
            {info.getValue()}
          </Text>
        ),
      }),
    ],
    [],
  );

  const table = useReviewTable(filtered, columns);

  if (products.length === 0) {
    return (
      <Text c="dimmed" mt="md">
        No unchanged products.
      </Text>
    );
  }

  return <ReviewTable table={table} highlightOnHover={false} minWidth={500} />;
}

// --- MSRP Changes Panel ---

const msrpColumnHelper = createColumnHelper<MsrpChange>();

const getMsrpText = (c: MsrpChange) => c.name;

function MsrpChangesPanel({
  changes,
  search,
  onAction,
}: {
  changes: MsrpChange[];
  search: string;
  onAction: (id: string, action: CatalogReviewAction) => void;
}) {
  const filtered = useSearchFilter(changes, search, getMsrpText);

  const columns = useMemo(
    () => [
      msrpColumnHelper.accessor("name", {
        header: "Product",
        cell: (info) => (
          <Text size="sm" fw={500}>
            {info.getValue()}
          </Text>
        ),
      }),
      msrpColumnHelper.accessor("previousMsrp", {
        header: "Old MSRP",
        meta: { align: "right" } as ColumnMeta,
        cell: (info) => (
          <Text size="sm" c="dimmed" td="line-through">
            {formatPriceCompact(info.getValue())}
          </Text>
        ),
      }),
      msrpColumnHelper.accessor("newMsrp", {
        header: "New MSRP",
        meta: { align: "right" } as ColumnMeta,
        cell: (info) => (
          <Text size="sm" fw={500}>
            {formatPriceCompact(info.getValue())}
          </Text>
        ),
      }),
      msrpColumnHelper.accessor("delta", {
        header: "Change",
        meta: { align: "right" } as ColumnMeta,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Group gap={4} justify="flex-end">
              <Text size="sm" fw={500} c={getDeltaColor(row.delta)}>
                {formatPriceChange(row.delta)}
              </Text>
              <Text size="xs" c={getDeltaColor(row.delta)}>
                ({formatPercentChange(row.percentChange)})
              </Text>
            </Group>
          );
        },
      }),
      msrpColumnHelper.accessor("reviewStatus", {
        header: "Action",
        meta: { align: "center" } as ColumnMeta,
        cell: (info) => {
          const row = info.row.original;
          return (
            <SegmentedControl
              size="xs"
              data={MSRP_CHANGE_OPTIONS}
              value={row.reviewStatus == null ? "decide_later" : row.reviewStatus}
              onChange={(val) => onAction(row.id, val as CatalogReviewAction)}
              aria-label={`Action for ${row.name}`}
            />
          );
        },
      }),
    ],
    [onAction],
  );

  const table = useReviewTable(filtered, columns);

  if (changes.length === 0) {
    return (
      <Text c="dimmed" mt="md">
        No MSRP changes detected.
      </Text>
    );
  }

  return <ReviewTable table={table} highlightPending emptyMessage="No changes match your search." />;
}
